import React, { useState, useEffect } from 'react';
import './App.css';

function App() {
  const [activities, setActivities] = useState([]);
  const [filteredActivities, setFilteredActivities] = useState([]);
  const [genderFilter, setGenderFilter] = useState('');
  const [ageFilter, setAgeFilter] = useState('');
  const [countFilter, setCountFilter] = useState('');
  const [costFilter, setCostFilter] = useState('');

  // Farbpalette für die Ränder
  const themeColors = ['#FF4500', '#FFD700', '#1E90FF', '#32CD32', '#800080', '#FFC0CB', '#FFA500', '#FF69B4'];

  useEffect(() => {
    fetch('/activities.json')
      .then(response => response.json())
      .then(data => setActivities(data));
  }, []);

  // Alter und Anzahl Kinder dynamisch berechnen
  const availableAges = Array.from(
    new Set(activities.flatMap(activity => Array.from({ length: activity.maxAge - activity.minAge + 1 }, (_, i) => activity.minAge + i)))
  ).sort((a, b) => a - b);

  const availableCounts = Array.from(
    new Set(activities.map(activity => activity.maxParticipants))
  ).sort((a, b) => a - b);

  // Funktion zur dynamischen Filterung
  useEffect(() => {
    // Prüfen, ob mindestens ein Filter gesetzt ist
    if (genderFilter || ageFilter || countFilter || costFilter) {
      const filtered = activities.filter(activity => {
        if (genderFilter && genderFilter !== 'both' && activity.gender !== genderFilter) {
          return false;
        }
        if (ageFilter && (ageFilter < activity.minAge || ageFilter > activity.maxAge)) {
          return false;
        }
        if (countFilter && countFilter > activity.maxParticipants) {
          return false;
        }
        if (costFilter && costFilter !== 'all' && activity.costLevel !== costFilter) {
          return false;
        }
        return true;
      });
      setFilteredActivities(filtered);
    } else {
      setFilteredActivities([]); // Keine Aktivitäten anzeigen, wenn keine Filter ausgewählt sind
    }
  }, [genderFilter, ageFilter, countFilter, costFilter, activities]);

  // Funktion, um eine zufällige Farbe aus der Palette zu wählen
  const getRandomColor = () => {
    return themeColors[Math.floor(Math.random() * themeColors.length)];
  };

  // Funktion, um jeden Buchstaben in einer zufälligen Farbe zu rendern
  const renderColorfulText = (text) => {
    return text.split('').map((letter, index) => (
      <span key={index} style={{ color: getRandomColor() }}>
        {letter}
      </span>
    ));
  };

  return (
    <div className="bg-blue-50 p-10 min-h-screen">
      <h1 className="text-5xl font-bold text-center mb-8">
        {renderColorfulText('Kunterbunte')} <span className="text-purple-900">Kinderideen</span>
      </h1>
      <h2 className="text-4xl font-bold text-center text-purple-900 mb-10">
        Aktivitäten für Kinder
      </h2>
      <p className="text-center text-gray-600 max-w-xl mx-auto mb-12">
        Wähle Filter aus, um passende Aktivitäten für Kinder anzuzeigen.
      </p>

      {/* Filter Container */}
      <div className="bg-white shadow-md rounded-lg p-6 mb-8 flex flex-wrap justify-center gap-6">
        <div>
          <label className="block text-lg font-semibold text-purple-700 mb-2">Geschlecht:</label>
          <select
              value={genderFilter}
              onChange={(e) => setGenderFilter(e.target.value)}
              className="border-2 border-purple-300 rounded-md p-2 text-gray-700 focus:outline-none focus:ring-2 focus:ring-purple-500"
          >
            <option value="">Bitte auswählen</option>
            <option value="both">Alle</option>
            <option value="boys">Jungs</option>
            <option value="girls">Mädchen</option>
          </select>
        </div>
        <div>
          <label className="block text-lg font-semibold text-purple-700 mb-2">Alter:</label>
          <select
              value={ageFilter}
              onChange={(e) => setAgeFilter(e.target.value)}
              className="border-2 border-purple-300 rounded-md p-2 text-gray-700 focus:outline-none focus:ring-2 focus:ring-purple-500"
          >
            <option value="">Wähle Alter</option>
            {availableAges.map(age => (
                <option key={age} value={age}>
                  {age}
                </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-lg font-semibold text-purple-700 mb-2">Anzahl Kinder:</label>
          <select
              value={countFilter}
              onChange={(e) => setCountFilter(e.target.value)}
              className="border-2 border-purple-300 rounded-md p-2 text-gray-700 focus:outline-none focus:ring-2 focus:ring-purple-500"
          >
            <option value="">Wähle Anzahl</option>
            {availableCounts.map(count => (
                <option key={count} value={count}>
                  {count}
                </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-lg font-semibold text-purple-700 mb-2">Kosten:</label>
          <select
              value={costFilter}
              onChange={(e) => setCostFilter(e.target.value)}
              className="border-2 border-purple-300 rounded-md p-2 text-gray-700 focus:outline-none focus:ring-2 focus:ring-purple-500"
          >
            <option value="">Bitte Auswählen</option>
            <option value="gering">Gering</option>
            <option value="mittel">Mittel</option>
            <option value="hoch">Hoch</option>
          </select>
        </div>
      </div>

      {/* Ergebnisse */}
      <div>
        {filteredActivities.length > 0 ? (
            <div className="flex flex-wrap justify-center gap-8">
              {filteredActivities.map((activity, index) => (
                  <div
                      key={activity.id}
                      className="bg-white rounded-xl shadow-lg p-6 w-72 text-center border-t-8"
                      style={{
                        borderTopColor: getRandomColor(), // Zufällige Farbe aus der Palette
                      }}
                  >
                    {activity.imageUrl && (
                        <img
                            className="w-24 h-24 mx-auto mb-4 rounded-full object-cover border-4 border-dashed border-gray-300"
                            src={activity.imageUrl || 'https://via.placeholder.com/150'}
                            alt={activity.name}
                        />
                    )}

                    <h3 className="text-lg font-semibold text-purple-700 mb-2">
                      {activity.name}
                    </h3>
                    <p className="text-gray-500 text-sm mb-4">
                      {activity.description}
                    </p>
                <div className="text-sm text-gray-600">
                  <p><strong>Alter:</strong> {activity.minAge} - {activity.maxAge}</p>
                  <p><strong>Max. Teilnehmer:</strong> {activity.maxParticipants}</p>
                  <p><strong>Kosten:</strong> {activity.costLevel}</p>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-center text-gray-600 text-lg mt-10">
            Keine Aktivitäten gefunden. Passen Sie die Filter an.
          </p>
        )}
      </div>
    </div>
  );
}

export default App;
